import { useQuery } from '@tanstack/react-query';
import { formatDate } from '@/utils/date';
import api from '@/lib/api';

interface Application {
  id: string;
  name: string;
  appKey: string;
}

interface UsersResponse {
  data: Array<{
    id: string;
    username: string;
    email: string;
    name: string;
    membershipType: string;
    membershipExpiresAt: string | null;
    applicationId: string | null;
    application: Application | null;
    createdAt: string;
  }>;
}

export default function UsersList() {
  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get<{ status: string; data: UsersResponse['data']; message?: string }>('/admin/users');
      console.log('[UsersList] API Response:', response.data);
      // response.data.data 是用户数组
      return response.data.data;
    },
    staleTime: 0,
    refetchOnMount: true,
  });

  // response.data.data 已经是数组了，不需要再 .data
  const users = data || [];

  console.log('[UsersList] Render - users:', users);
  console.log('[UsersList] Render - users.length:', users.length);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">用户管理</h1>
        <p className="mt-1 text-sm text-gray-600">
          管理用户账户和会员信息
        </p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  姓名
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  用户名
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  邮箱
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  所属应用
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  会员类型
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  到期时间
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  注册时间
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    加载中...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    暂无用户
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {user.application ? (
                        <div>
                          <div className="font-medium text-gray-900">{user.application.name}</div>
                          <div className="text-xs text-gray-500 font-mono">{user.application.appKey}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">未分配</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        user.membershipType === 'yearly' ? 'bg-purple-100 text-purple-800' :
                        user.membershipType === 'monthly' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.membershipType === 'yearly' ? '年度会员' : user.membershipType === 'monthly' ? '月度会员' : '免费用户'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.membershipExpiresAt
                        ? formatDate(user.membershipExpiresAt)
                        : '无'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(user.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
